import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getListPlatformSupportSessionsQueryKey, getListMyEligibleRolesQueryKey, getListMyPermanentTenantRoleOptionsQueryKey, useActivateMyElevation, useEndMyElevation, useEndPlatformSupportSession, useGrantMyPermanentTenantRole, useListMyEligibleRoles, useListMyPermanentTenantRoleOptions } from '@cdf/api-client-react';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Textarea } from '@/components/ui/textarea';
import { ElevationRequests } from './elevation-requests';
import { RecoveryAccessPanel } from './recovery-access-panel';
import { useTenantEntity } from './tenant-entity-provider';

export function useAccessClock() {
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

export function remainingAccess(expiresAt: string, now: number) {
  const seconds = Math.max(0, Math.ceil((Date.parse(expiresAt) - now) / 1000));
  if (!seconds) return 'Expired';
  return `${Math.floor(seconds / 3600)}h ${Math.floor(seconds % 3600 / 60)}m ${seconds % 60}s remaining`;
}

function message(error: unknown) {
  return error instanceof Error ? error.message : 'Could not update access. Please try again.';
}

interface ElevateAccessDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose?: () => void;
  onStartSupportAccess?: () => void;
  tenantId?: string;
  tenantName?: string;
}

export function ElevateAccessDrawer({ open, onOpenChange, onClose, onStartSupportAccess, tenantId, tenantName }: ElevateAccessDrawerProps) {
  const access = useTenantEntity();
  const queryClient = useQueryClient();
  const [offset, setOffset] = useState(0);
  const roles = useListMyEligibleRoles({ limit: 25, offset }, { query: { queryKey: getListMyEligibleRolesQueryKey({ limit: 25, offset }), enabled: open } });
  const activate = useActivateMyElevation();
  const end = useEndMyElevation();
  const [role, setRole] = useState('');
  const [reason, setReason] = useState('');
  const [duration, setDuration] = useState('240');
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [permanentOffset, setPermanentOffset] = useState(0);
  const [permanentRole, setPermanentRole] = useState('');
  const [permanentReason, setPermanentReason] = useState('');
  const [permanentSuccess, setPermanentSuccess] = useState<string | null>(null);
  const now = useAccessClock();
  const availableRoles = (roles.data?.items ?? []).filter((item) => !tenantId || item.tenantId === tenantId);
  const activeElevations = access.activeElevations.filter((item) => !tenantId || item.tenantId === tenantId);
  const hasTenantElevation = activeElevations.some((item) => item.entityId === null);
  const shouldCheckPermanentAccess = Boolean(tenantId && hasTenantElevation && access.hasPlatformPermission('platform.roles.manage'));
  const permanentRoles = useListMyPermanentTenantRoleOptions(
    { tenantId: tenantId ?? '', limit: 25, offset: permanentOffset },
    { query: { queryKey: getListMyPermanentTenantRoleOptionsQueryKey({ tenantId: tenantId ?? '', limit: 25, offset: permanentOffset }), enabled: open && shouldCheckPermanentAccess } }
  );
  // A successful options response is the authoritative presentation gate. It
  // prevents a temporary platform elevation from exposing this permanent action.
  const canGrantPermanentAccess = shouldCheckPermanentAccess && permanentRoles.isSuccess;
  const grantPermanent = useGrantMyPermanentTenantRole();
  const busy = activate.isPending || end.isPending || grantPermanent.isPending;
  const hasMultipleRolePages = offset > 0 || (roles.data?.total ?? 0) > 25;
  const hasMultiplePermanentRolePages = permanentOffset > 0 || (permanentRoles.data?.total ?? 0) > 25;
  useEffect(() => {
    if (!open) return;
    setOffset(0);
    setRole('');
    setReason('');
    setDuration('240');
    setError(null);
    setPermanentOffset(0);
    setPermanentRole('');
    setPermanentReason('');
    setPermanentSuccess(null);
  }, [open, tenantId]);
  const submit = async () => {
    if (!role || reason.trim().length < 10 || Number(duration) < 60 || Number(duration) > 1440) return;
    setError(null);
    try {
      await activate.mutateAsync({ data: { eligibleAssignmentId: role, reason: reason.trim(), durationMinutes: Number(duration) } });
      setRole('');
      setReason('');
      setSubmitted(true);
      await queryClient.invalidateQueries();
      await access.refresh();
    } catch (error) { setError(message(error)); }
  };
  const endElevation = async (id: string) => {
    setError(null);
    try {
      await end.mutateAsync({ id, data: { reason: 'Elevation ended by the user.' } });
      await access.refresh();
    } catch (error) { setError(message(error)); }
  };
  const grantPermanentAccess = async () => {
    if (!tenantId || !permanentRole || permanentReason.trim().length < 10) return;
    setError(null);
    setPermanentSuccess(null);
    try {
      const granted = await grantPermanent.mutateAsync({ data: {
        tenantId,
        roleDefinitionId: permanentRole,
        reason: permanentReason.trim()
      } });
      setPermanentRole('');
      setPermanentReason('');
      setPermanentSuccess(`${granted.roleName} is now permanent. Your tenant membership is active and the change is recorded in the security audit.`);
      await queryClient.invalidateQueries({ queryKey: getListMyPermanentTenantRoleOptionsQueryKey() });
      await access.refresh();
    } catch (error) { setError(message(error)); }
  };
  return <Sheet open={open} onOpenChange={(nextOpen) => { if (!busy || nextOpen) onOpenChange(nextOpen); }}>
    <SheetContent side="right" className="flex h-full w-full max-w-none flex-col overflow-y-auto p-0 sm:max-w-lg" onCloseAutoFocus={(event) => { if (onClose) { event.preventDefault(); onClose(); } }}>
      <SheetHeader className="shrink-0 border-b px-6 py-5 pr-12">
        <SheetTitle>Elevate access{tenantName ? ` · ${tenantName}` : ''}</SheetTitle>
        <SheetDescription>Request an eligible role for 1–24 hours. Another administrator must approve it. Your reason and their decision are recorded in the security audit.</SheetDescription>
      </SheetHeader>
      <div className="flex-1 space-y-6 px-6 py-5">
      <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        {roles.isPending ? <p role="status">Loading eligible roles…</p> : roles.isError ? <div role="alert">Could not load eligible roles. <Button type="button" variant="outline" onClick={() => void roles.refetch()}>Try again</Button></div> : <>
          {availableRoles.length > 0 ? <>
            <div className="space-y-2"><Label htmlFor="elevation-role">Role</Label><Select value={role} onValueChange={setRole}><SelectTrigger id="elevation-role"><SelectValue placeholder="Choose an eligible role" /></SelectTrigger><SelectContent>
              {availableRoles.map((item) => <SelectItem key={item.id} value={item.id}>{item.roleName} · {tenantName ?? (item.entityId ? 'Entity' : item.tenantId ? 'Tenant' : 'Platform')}</SelectItem>)}
            </SelectContent></Select></div>
            <div className="space-y-2"><Label htmlFor="elevation-reason">Reason (at least 10 characters)</Label><Textarea id="elevation-reason" required minLength={10} maxLength={1000} value={reason} onChange={(event) => setReason(event.target.value)} /></div>
            <div className="space-y-2"><Label htmlFor="elevation-duration">Duration</Label><Select value={duration} onValueChange={setDuration}><SelectTrigger id="elevation-duration"><SelectValue /></SelectTrigger><SelectContent>{[60, 120, 240, 480, 720, 1440].map((minutes) => <SelectItem key={minutes} value={String(minutes)}>{minutes < 60 ? `${minutes} minutes` : `${minutes / 60} ${minutes === 60 ? 'hour' : 'hours'}`}</SelectItem>)}</SelectContent></Select></div>
            <Button type="submit" disabled={busy || !role || reason.trim().length < 10}>Request access</Button>
          </> : <div role="status" className="space-y-3 rounded-md border border-dashed p-4 text-sm">
            <p className="font-medium">No current role eligibility</p>
            <p className="text-muted-foreground">{tenantName ? hasMultipleRolePages ? `No eligible roles for ${tenantName} appear on this page. Check the other role pages.` : `You do not currently have an eligible role for ${tenantName}. A tenant access administrator can grant or renew role eligibility.` : 'You do not currently have an eligible role.'}</p>
            {onStartSupportAccess && <Button type="button" variant="outline" onClick={onStartSupportAccess}>Start audited support access instead</Button>}
          </div>}
          {hasMultipleRolePages && <div className="flex items-center gap-2"><Button type="button" variant="outline" disabled={offset === 0 || roles.isFetching} onClick={() => { setOffset(offset - 25); setRole(''); }}>Previous roles</Button><Button type="button" variant="outline" disabled={offset + 25 >= (roles.data?.total ?? 0) || roles.isFetching} onClick={() => { setOffset(offset + 25); setRole(''); }}>Next roles</Button></div>}
        </>}
      </form>
      {submitted && <p role="status">Request submitted. Access starts only after another administrator approves it.</p>}
      {error && <p role="alert" className="text-sm">{error}</p>}
      <ElevationRequests />
      {access.tenants.filter(t => t.permissions?.includes('tenant.roles.assign')).map(t => <div key={t.id}><h3 className="font-medium">{t.name}</h3><ElevationRequests scope="tenant" tenantId={t.id} /></div>)}
      {access.hasPlatformPermission('platform.elevation.manage') && <ElevationRequests scope="platform" />}
      {activeElevations.length > 0 && <section className="space-y-3" aria-label="Active elevations"><h3 className="font-medium">Active elevations</h3>{activeElevations.map((item) => <div key={item.id} className="flex items-center justify-between gap-3 rounded-md border p-3"><div><p className="text-sm font-medium">{item.roleName}</p><p className="text-muted-foreground text-xs">{remainingAccess(item.expiresAt, now)}</p></div><Button variant="outline" disabled={busy} onClick={() => void endElevation(item.id)}>End now</Button></div>)}</section>}
      <RecoveryAccessPanel />
      {canGrantPermanentAccess && <section className="space-y-4 border-t pt-5" aria-labelledby="permanent-access-title">
        <div className="space-y-1"><h3 id="permanent-access-title" className="font-medium">Make access permanent</h3><p className="text-muted-foreground text-sm">Grant yourself a tenant role that stays within your active elevation. If needed, your tenant membership will be created or reactivated. Every change is audited.</p></div>
        {(permanentRoles.data?.items.length ?? 0) > 0 ? <form className="space-y-4" onSubmit={(event) => { event.preventDefault(); void grantPermanentAccess(); }}>
          <div className="space-y-2"><Label htmlFor="permanent-tenant-role">Permanent role</Label><Select value={permanentRole} onValueChange={setPermanentRole}><SelectTrigger id="permanent-tenant-role"><SelectValue placeholder="Choose a role within your elevation" /></SelectTrigger><SelectContent>
            {permanentRoles.data!.items.map((item) => <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>)}
          </SelectContent></Select></div>
          <div className="space-y-2"><Label htmlFor="permanent-access-reason">Reason (at least 10 characters)</Label><Textarea id="permanent-access-reason" required minLength={10} maxLength={1000} value={permanentReason} onChange={(event) => setPermanentReason(event.target.value)} /></div>
          <Button type="submit" disabled={busy || !permanentRole || permanentReason.trim().length < 10}>Grant permanent access</Button>
        </form> : <p role="status" className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">No additional tenant roles fit within your active elevation.</p>}
        {hasMultiplePermanentRolePages && <div className="flex items-center gap-2"><Button type="button" variant="outline" disabled={permanentOffset === 0 || permanentRoles.isFetching} onClick={() => { setPermanentOffset(permanentOffset - 25); setPermanentRole(''); }}>Previous permanent roles</Button><Button type="button" variant="outline" disabled={permanentOffset + 25 >= (permanentRoles.data?.total ?? 0) || permanentRoles.isFetching} onClick={() => { setPermanentOffset(permanentOffset + 25); setPermanentRole(''); }}>Next permanent roles</Button></div>}
        {permanentSuccess && <p role="status" className="text-sm">{permanentSuccess}</p>}
      </section>}
      </div>
    </SheetContent>
  </Sheet>;
}

export function SupportAccessBanner({ inMenu = false }: { inMenu?: boolean }) {
  const access = useTenantEntity();
  const queryClient = useQueryClient();
  const end = useEndPlatformSupportSession();
  const [error, setError] = useState<string | null>(null);
  const now = useAccessClock();
  const finish = async (id: string) => {
    setError(null);
    try {
      await end.mutateAsync({ id, data: { reason: 'Support session ended by the user.' } });
      await queryClient.invalidateQueries({ queryKey: getListPlatformSupportSessionsQueryKey() });
      await access.refresh();
    } catch (error) { setError(message(error)); }
  };
  if (!access.activeSupportSessions.length) return null;
  return <section aria-label="Support access" className="max-h-40 shrink-0 overflow-y-auto border-b bg-muted px-4 py-2 text-sm">
    {access.activeSupportSessions.map((item) => <div key={item.id} className="flex flex-wrap items-center justify-between gap-2">
      <p><strong>Support access</strong> · {access.tenants.find((tenant) => tenant.id === item.tenantId)?.name ?? 'Tenant support session'} · {remainingAccess(item.expiresAt, now)}</p>
      {inMenu ? <DropdownMenuItem disabled={end.isPending} onSelect={(event) => { event.preventDefault(); void finish(item.id); }}>End session</DropdownMenuItem> : <Button size="sm" variant="outline" disabled={end.isPending} onClick={() => void finish(item.id)}>End session</Button>}
    </div>)}
    {error && <p role="alert">{error}</p>}
  </section>;
}
